/** @format */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import galaxieClient from "@/api/galaxieClient";
import { useUser } from "@/api/userAPI";

export interface CalendarFeedPreferences {
  id: string;
  enabled: boolean;
  runTypes: number[];
  seriesNids: number[];
  subscribeUrl: string | null;
  updatedAt: string;
}

const QUERY_KEY = ["calendar-feed"] as const;

export function useCalendarFeed() {
  const queryClient = useQueryClient();
  const { data: user } = useUser();

  const { data, isLoading, error } = useQuery<CalendarFeedPreferences>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await galaxieClient.get("/calendar/preferences/");
      return response.data as CalendarFeedPreferences;
    },
    enabled: !!user?.id,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<CalendarFeedPreferences>) => {
      const response = await galaxieClient.patch("/calendar/preferences/", updates);
      return response.data as CalendarFeedPreferences;
    },
    onSuccess: updated => {
      queryClient.setQueryData(QUERY_KEY, updated);
    },
  });

  const rotateMutation = useMutation({
    mutationFn: async () => {
      const response = await galaxieClient.post("/calendar/preferences/rotate-token/");
      return response.data as CalendarFeedPreferences;
    },
    onSuccess: rotated => {
      queryClient.setQueryData(QUERY_KEY, rotated);
    },
  });

  return {
    feed: data,
    isLoading,
    error,
    updateFeed: updateMutation.mutate,
    updateFeedAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    rotateToken: rotateMutation.mutate,
    isRotating: rotateMutation.isPending,
  };
}
